import { call, put, select } from 'redux-saga/effects';
import nsToken from '@netsapiens/netsapiens-js/dist/token';
import nsDevice from '@netsapiens/netsapiens-js/dist/device';
import i18n from 'i18next';
import bugsnagClient from '../../../services/bugsnag/bugsnag';
import store from '../../../state/store';
import { setUAAuthorized } from '../../../state/ua/uaSlice';
import { selectWebphoneDevice } from '../../../state/webphoneDevice/webphoneDeviceSlice';
import { selectDevicePostFix } from '../../../state/configs/configsSlice';
import { setSnackbar } from '../../../state/snackbar/snackbarSlice';
import { initiateReconnectAction } from '../initiateReconnect/action';

/**
 * Handles UA unauthorized (401) status after register attempt
 * Fetches the current audio device and compares authorization key
 * with the device stored in Redux to determine if credentials have changed
 *
 * This saga is typically called when the SIP registration receives a 401 status,
 * indicating that the device credentials may have been updated elsewhere
 */
export default function* handleUAUnAuthorized() {
  try {
    console.debug('handleUAUnAuthorized: Starting unauthorized status handling');

    const decodedToken = yield call(nsToken.getDecoded);
    const devicePostFix = yield select(selectDevicePostFix);
    const currentDevice = yield select(selectWebphoneDevice);

    if (!currentDevice) {
      console.debug('handleUAUnAuthorized: No current device found in Redux state');
      return;
    }

    // Fetch the latest device information from the server
    const deviceUri = `sip:${decodedToken.user}${devicePostFix}@${decodedToken.domain}`;

    console.debug('handleUAUnAuthorized: Fetching device info', {
      deviceUri,
      currentDeviceAuthKey: currentDevice.authentication_key ? '[REDACTED]' : 'undefined',
    });

    let fetchedDevice;
    try {
      fetchedDevice = yield call(nsDevice.get, {
        device: deviceUri,
        domain: decodedToken.domain,
        userId: decodedToken.user,
        createDevice: false, // Don't create if it doesn't exist
      });
    } catch (fetchError) {
      console.debug('handleUAUnAuthorized: Failed to fetch device from server', fetchError);
      bugsnagClient.notify(fetchError);
      return;
    }

    if (!fetchedDevice) {
      console.debug('handleUAUnAuthorized: Device not found on server');
      return;
    }

    // Compare authorization keys
    const currentAuthKey = currentDevice.authentication_key;
    const fetchedAuthKey = fetchedDevice.authentication_key;
    if (currentAuthKey !== fetchedAuthKey) {
      console.debug('handleUAUnAuthorized: Authorization keys do not match - device credentials have changed');
      yield put(setUAAuthorized(false));

      yield put(setSnackbar({
        open: true,
        duration: 0,
        message: `${i18n.t('Disconnected by another instance')}`,
        type: 'ERROR',
        action: {
          label: i18n.t('RECONNECT'),
          link: () => {
            // Dispatch a custom action that will be handled by a saga watcher
            store.dispatch(initiateReconnectAction());
          },
        },
      }));
    } else {
      console.debug('handleUAUnAuthorized: Authorization keys match - 401 may be temporary');
      // Keys match but we still got 401 - this might be a temporary network issue
      // Keep authorized as false for now to trigger re-registration
    }
  } catch (error) {
    console.debug('handleUAUnAuthorized: Error in unauthorized handling', error);
    bugsnagClient.notify(error);
  }
}
